import {
  AuthProvider,
  Button,
  Card,
  Empty,
  Link,
  useAuthProvider,
  useRoute,
} from "./components";
import type { AuthValue } from "./components";
import { ChangePasswordPage, LoginPage } from "./routes/AuthPages";
import {
  AccountsPage,
  AdminLeaguePage,
  AdminNewLeaguePage,
  LeagueDirectoryPage,
} from "./routes/AdminPages";
import { EntryPickPage, EntryRoundPage } from "./routes/EntryPages";
import {
  JoinPage,
  LeagueIndexPage,
  LeaguePage,
  RoundPage,
  ShooterPage,
  StandingsPage,
} from "./routes/PublicPages";
import { useThemePreference } from "./theme";

const THEME_LABELS = { system: "device", light: "light", dark: "dark" } as const;

export default function App() {
  const auth = useAuthProvider();

  return (
    <AuthProvider value={auth}>
      <div className="shell">
        <Masthead auth={auth} />
        <Main auth={auth} />
        <SiteFooter />
      </div>
    </AuthProvider>
  );
}

function Masthead({ auth }: { auth: AuthValue }) {
  const { user, loading, signOut } = auth;

  return (
    <header className="masthead">
      <Link to="/" className="wordmark">
        Aimly
      </Link>
      <nav className="nav" aria-label="Main">
        {loading ? (
          <span className="muted">…</span>
        ) : user === null ? (
          <Link to="/login" className="btn btn-ghost">
            Sign in
          </Link>
        ) : (
          <>
            <Link to="/admin" className="btn btn-ghost">
              My leagues
            </Link>
            <Button variant="ghost" onClick={() => void signOut().then(() => window.location.assign("/"))}>
              Sign out
            </Button>
          </>
        )}
        <ThemeToggle />
      </nav>
    </header>
  );
}

function Main({ auth }: { auth: AuthValue }) {
  const route = useRoute();
  const { user, loading } = auth;

  // A forced password change is an interstitial, not a page you can navigate away from:
  // every authenticated write answers 403 until it is done, so showing the rest of the app
  // would only produce errors.
  if (!loading && user !== null && user.must_change_password) {
    return (
      <main>
        <ChangePasswordPage forced />
      </main>
    );
  }

  return (
    <main>
      <Body route={route} />
    </main>
  );
}

function Body({ route }: { route: ReturnType<typeof useRoute> }) {
  switch (route.name) {
    case "index":
      return <LeagueIndexPage />;
    case "login":
      return <LoginPage />;
    case "password":
      return <ChangePasswordPage />;
    case "admin.index":
      return <LeagueDirectoryPage />;
    case "admin.new":
      return <AdminNewLeaguePage />;
    case "admin.accounts":
      return <AccountsPage />;
    case "admin.league":
      return <AdminLeaguePage slug={route.params.slug} />;
    case "entry.pick":
      return <EntryPickPage slug={route.params.slug} />;
    case "entry.round":
      return <EntryRoundPage slug={route.params.slug} roundId={route.params.roundId} />;
    case "league.standings":
      return <StandingsPage slug={route.params.slug} />;
    case "league.join":
      return <JoinPage slug={route.params.slug} />;
    case "league.round":
      return <RoundPage slug={route.params.slug} roundId={route.params.roundId} />;
    case "league.shooter":
      return <ShooterPage slug={route.params.slug} shooterId={route.params.shooterId} />;
    case "league.view":
      return <LeaguePage slug={route.params.slug} />;
    default:
      return (
        <Card>
          <Empty
            title="Nothing at that address"
            note="Leagues live at /league-slug. Start from the league directory."
            action={
              <Link to="/" className="btn btn-primary">
                League directory
              </Link>
            }
          />
        </Card>
      );
  }
}

function ThemeToggle() {
  const [preference, cycle] = useThemePreference();
  return (
    <button type="button" className="theme-toggle" onClick={cycle}>
      Theme: {THEME_LABELS[preference]}
    </button>
  );
}

function SiteFooter() {
  return (
    <footer className="footer">
      <span>Live scores are public the instant they are saved. Nothing here is cached.</span>
    </footer>
  );
}
